import React, { useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import { loginRequest } from '../authConfig';

const SUPPORTED_SETTINGS = {
  MinAge: {
    label: 'Minimum Age',
    description: 'Minimum age required for a potential juror.',
    category: 'Eligibility',
    control: 'number'
  },
  MaxAge: {
    label: 'Maximum Age',
    description: 'Maximum age allowed for a potential juror.',
    category: 'Eligibility',
    control: 'number'
  },
  Nationality: {
    label: 'Allowed Nationalities',
    description: 'Comma-separated nationalities accepted from the live EBC source view. Leave blank to allow all nationalities returned by the source.',
    category: 'Eligibility',
    control: 'text'
  },
  PersonsPerConstituency: {
    label: 'Persons Per Constituency',
    description: 'Maximum number of persons selected from any one constituency in a run.',
    category: 'Selection',
    control: 'number'
  },
  PersonsPerRun: {
    label: 'Persons Per Run',
    description: 'Target total number of persons selected across the full run, distributed across constituencies subject to the constituency cap.',
    category: 'Selection',
    control: 'number'
  },
  MaxSelectionsPerYear: {
    label: 'Max Selections Per Year',
    description: 'How many times the same juror may be selected within a year.',
    category: 'Selection',
    control: 'number'
  },
  AllowConsecutiveService: {
    label: 'Allow Consecutive Service',
    description: 'Whether a juror may appear in consecutive selection pools.',
    category: 'Selection',
    control: 'select',
    options: ['Yes', 'No']
  },
  PoliceVettingMode: {
    label: 'Police Vetting Mode',
    description: 'Operational mode label for the police vetting workflow used by this run.',
    category: 'Vetting',
    control: 'select',
    options: ['Hybrid', 'InAppOnly', 'BatchImportOnly']
  },
  PoolExpiryDays: {
    label: 'Pool Expiry Days',
    description: 'How long a generated pool remains active before it should be regenerated.',
    category: 'Vetting',
    control: 'number'
  }
};

const CATEGORY_DESCRIPTIONS = {
  Eligibility: 'The current live source filters enforce active records, the configured nationality list, and the configured age range.',
  Selection: 'These settings control total run size, constituency caps, and repeat-selection rules used during pool generation.',
  Vetting: 'These settings describe the current police workflow and how long a generated pool stays current.'
};

function SettingsPage() {
  const { instance, accounts } = useMsal();
  const [settings, setSettings] = useState([]);
  const [edited, setEdited] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingName, setSavingName] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const groupedSettings = useMemo(() => {
    return settings.reduce((groups, item) => {
      const metadata = SUPPORTED_SETTINGS[item.Name];
      if (!metadata) {
        return groups;
      }

      const category = metadata.category || item.Category || 'General';
      groups[category] = groups[category] || [];
      groups[category].push({
        ...item,
        Category: category,
        Description: metadata.description || item.Description,
        DisplayLabel: metadata.label || item.Name,
        UiControl: metadata.control || (item.Type === 'int' ? 'number' : 'text'),
        Options: metadata.options || []
      });
      return groups;
    }, {});
  }, [settings]);

  const getAccessToken = async () => {
    const account = accounts[0];
    const tokenResponse = await instance.acquireTokenSilent({
      scopes: loginRequest.scopes,
      account
    });

    return tokenResponse.accessToken;
  };

  const loadSettings = async () => {
    setLoading(true);
    setError('');

    try {
      const accessToken = await getAccessToken();
      const response = await fetch('/api/settings', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Unable to load settings');
      }

      setSettings(data);
      setEdited({});
    } catch (err) {
      console.error('Error loading settings', err);
      setError(err.message || 'Unable to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (name, value) => {
    setEdited((current) => ({ ...current, [name]: value }));
    setMessage('');
  };

  const saveSetting = async (name) => {
    setSavingName(name);
    setMessage('');
    setError('');

    try {
      const accessToken = await getAccessToken();
      const response = await fetch(`/api/settings/${encodeURIComponent(name)}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ value: edited[name] })
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Unable to save setting');
      }

      setMessage(data.message || `${name} updated`);
      await loadSettings();
    } catch (err) {
      console.error('Error saving setting', err);
      setError(err.message || 'Unable to save setting');
    } finally {
      setSavingName('');
    }
  };

  return (
    <Stack spacing={3}>
      <Paper elevation={0} sx={{ p: 3.5, border: '1px solid rgba(13, 59, 102, 0.10)' }}>
        <Stack spacing={1.5}>
          <Chip label="Policy & Configuration" color="primary" variant="outlined" sx={{ alignSelf: 'flex-start' }} />
          <Typography variant="h2">Selection Rules & Workflow Settings</Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 780 }}>
            This area now focuses on settings that the current application actually uses during
            selection and police vetting. The live source currently enforces active `RecordStatus`,
            the configured nationality list, and the configured age range. Environment-managed
            connection values and hidden secrets stay out of this screen to avoid misleading edits.
          </Typography>
        </Stack>
      </Paper>

      {message ? <Alert severity="success">{message}</Alert> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Alert severity="info">
        SQL connection settings and the police import shared secret are managed through environment
        variables, not this Settings screen. The confirmed live source view currently exposes
        `RecordStatus`, `Nationality`, `DateOfBirth`, and `Age`, so source-side filtering is
        enforced against active records plus the configured nationality and age rules.
      </Alert>

      {loading ? (
        <Paper elevation={0} sx={{ p: 6, border: '1px solid rgba(13, 59, 102, 0.10)' }}>
          <Stack alignItems="center" spacing={2}>
            <CircularProgress />
            <Typography color="text.secondary">Loading configuration…</Typography>
          </Stack>
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }
          }}
        >
          {Object.entries(groupedSettings).map(([category, items]) => (
            <Paper key={category} elevation={0} sx={{ p: 3, border: '1px solid rgba(13, 59, 102, 0.10)' }}>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h4">{category}</Typography>
                  <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                    {CATEGORY_DESCRIPTIONS[category] || 'Review and update the rules that govern this part of the jury-selection workflow.'}
                  </Typography>
                </Box>

                {items.map((item) => {
                  const currentValue = edited[item.Name] ?? item.Value ?? '';
                  const isSensitive = item.IsSensitive;
                  const isSaving = savingName === item.Name;

                  return (
                    <Stack key={item.Name} spacing={1.25} sx={{ pb: 1.5 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <Box>
                          <Typography variant="h6">{item.DisplayLabel || item.Name}</Typography>
                          <Typography color="text.secondary">{item.Description}</Typography>
                        </Box>
                        <Chip size="small" label={item.Type || 'string'} variant="outlined" />
                      </Stack>
                      <TextField
                        fullWidth
                        name={item.Name}
                        label={item.DisplayLabel || item.Name}
                        value={currentValue}
                        onChange={(event) => handleChange(item.Name, event.target.value)}
                        type={item.UiControl === 'number' ? 'number' : 'text'}
                        select={item.UiControl === 'select'}
                        autoComplete="off"
                        helperText={isSensitive ? 'Sensitive value is masked in reads. Enter a replacement only when rotating it.' : ' '}
                        InputProps={{
                          readOnly: false
                        }}
                      >
                        {item.UiControl === 'select'
                          ? item.Options.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))
                          : null}
                      </TextField>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography color="text.secondary">
                          {isSensitive ? 'Stored securely; do not expose in screenshots or exports.' : 'Changes should be audited and approved.'}
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<SaveRoundedIcon />}
                          disabled={isSaving || edited[item.Name] === undefined}
                          onClick={() => saveSetting(item.Name)}
                        >
                          {isSaving ? 'Saving…' : 'Save'}
                        </Button>
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
            </Paper>
          ))}
        </Box>
      )}
    </Stack>
  );
}

export default SettingsPage;
